import React, { useState, useEffect } from "react";
import './Login.scss';
import axios from 'axios';
import { resendOtp, resendOtpByEmail, resendOtpByPhone, resendOtpByWhatsApp } from '../../../api/messaging';
import { setPreferredNotifyChannel } from '../../../utils/notifyChannel';
import { useNavigate, Link } from "react-router-dom";
import { GuestForm } from "../../GuestForm";
import { useNoProfile } from "../../../hooks/useProfileData";
import { useProfile } from "../../../Contexts/ProfileContext";
import { clearGuestData } from "../../../utils/storage";
import { GoogleSignInButton } from "../../GoogleSignInButton";
import { noAutofillProps } from "../../noAutofillProps";
import { requestLoginOtpAndNavigate } from "../../otpCooldown";
import { isValidLoginEmail } from "../../loginIdentifier";
import { usePasswordSetCheck } from "../../usePasswordSetCheck";
import { fetchSiteMedia, mediaUrl } from "../../../utils/siteMedia";

export function MobLogin() {
  useNoProfile();
  const { fetchProfile, updateProfileData } = useProfile();
  const [authBackground, setAuthBackground] = useState('');

  useEffect(() => {
    localStorage.removeItem('resendTime');
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSiteMedia().then((assets) => {
      if (!cancelled) setAuthBackground(mediaUrl(assets, 'login.background') || '');
    });
    return () => { cancelled = true; };
  }, []);

  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [showGuestForm, setShowGuestForm] = useState(false);
  const navigate = useNavigate();

  const hasValidPhone = formData.phone.length === 10;
  const hasValidEmail = isValidLoginEmail(formData.email);
  const hasValidIdentifier = hasValidPhone || hasValidEmail;
  const {
    hasPasswordSet,
    checkingPasswordSet,
    isPasswordCheckReady,
    isPasswordCheckPending,
    usesPhoneIdentifier,
  } = usePasswordSetCheck(formData.phone, formData.email);
  const showPasswordField = isPasswordCheckReady && hasPasswordSet === true;
  const showOtpAlternatives = isPasswordCheckReady;
  const showPhoneOtpOptions = showOtpAlternatives && usesPhoneIdentifier;
  const showEmailOtpOption = showOtpAlternatives && !usesPhoneIdentifier;

  const getSubmitButtonLabel = () => {
    if (loading) {
      if (hasPasswordSet === false) {
        return usesPhoneIdentifier ? 'Sending OTP...' : 'Sending email code...';
      }
      return 'Logging in...';
    }
    if (isPasswordCheckPending) {
      return 'Checking...';
    }
    if (isPasswordCheckReady && hasPasswordSet === false) {
      return usesPhoneIdentifier ? 'Continue with OTP' : 'Continue with email code';
    }
    return 'LOGIN';
  };

  useEffect(() => {
    if (hasPasswordSet !== true) {
      setFormData((prev) => (prev.password ? { ...prev, password: '' } : prev));
    }
  }, [hasPasswordSet]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'phone') {
      const phone = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        phone,
        ...(phone ? { email: '' } : {}),
      }));
      return;
    }
    if (name === 'email') {
      setFormData((prev) => ({
        ...prev,
        email: value,
        ...(value.trim() ? { phone: '' } : {}),
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!hasValidIdentifier) {
      setResponseMessage('Please enter a valid 10-digit mobile number or email address.');
      return;
    }

    if (isPasswordCheckReady && hasPasswordSet === true && !formData.password.trim()) {
      setResponseMessage('Password is required.');
      return;
    }

    setLoading(true);
    setResponseMessage('');

    try {
      if (isPasswordCheckReady && hasPasswordSet === false) {
        const type = usesPhoneIdentifier ? 'phone' : 'email';
        const identifier = usesPhoneIdentifier ? formData.phone : formData.email.trim();
        await requestLoginOtpAndNavigate({
          type,
          identifier,
          navigate,
          sendFn: () => resendOtp(
            usesPhoneIdentifier
              ? { phone: formData.phone, purpose: 'login' }
              : { email: formData.email.trim(), purpose: 'login' }
          ),
        });
      } else {
        const payload = {
          password: formData.password,
          ...(usesPhoneIdentifier ? { phone: formData.phone } : { email: formData.email.trim() })
        };
        const response = await axios.post('/users/login', payload);
        if (response.status === 200 && response.data?.data) {
          const token = response.data.data.token;
          if (token) {
            clearGuestData();
            localStorage.setItem('token', token);
            localStorage.setItem('passwordSet', 'true'); // User logged in with password
            await fetchProfile(true);
            updateProfileData({ data: { passwordSet: true } });
          }
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.response?.data?.msg || error.response?.data?.message;
      setResponseMessage(msg || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mob-authPage">
      <div className="auth-container">
        <div className="LazyLoad is-visible auth-hero">
          {authBackground ? (
          <img
            src={authBackground}
            className="img-responsive preLoad loaded"
            alt=""
          />
          ) : null}
        </div>
        <div className="signInContainer">
          <div className="welcome-header">
            Login
            <span className="welcome-header-small">Welcome back!</span>
          </div>
          <div className="mobileInputContainer">
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="auth-identifier-group">
                <div className="form-group">
                  <input
                    type="tel"
                    name="phone"
                    className="form-control mobileNumberInput"
                    placeholder="Mobile Number"
                    maxLength="10"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={checkingPasswordSet}
                    inputMode="numeric"
                    {...noAutofillProps()}
                  />
                <span className="placeholderAlternative mobileNumber">
                  +91
                  <span style={{ padding: '0px 10px', position: 'relative', bottom: '1px' }}>|</span>
                  Mobile Number <span className="field-optional-label">(or email below)</span>
                </span>
                <i className="bar"></i>
              </div>

              <div className="auth-field-or">OR</div>

              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  className="form-control mobileNumberInput"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={checkingPasswordSet}
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber">
                  Email Address <span className="field-optional-label">(or mobile above)</span>
                </span>
                <i className="bar"></i>
              </div>
              </div>

              {(showPasswordField || showOtpAlternatives) && (
                <div
                  className={`login-verify-group${showPasswordField ? ' login-verify-group--with-password' : ''}`}
                >
                  <span className="login-verify-group__label">
                    {showPasswordField ? 'Sign in with' : 'Verify using'}
                  </span>

                  {showPasswordField && (
                    <div className="form-group">
                      <input
                        type="password"
                        name="password"
                        className="form-control mobileNumberInput"
                        placeholder="Password"
                        value={formData.password}
                        onChange={handleInputChange}
                        {...noAutofillProps({ autoComplete: 'new-password' })}
                      />
                      <span className="placeholderAlternative mobileNumber">
                        Password <span className="field-optional-label">(or OTP below)</span>
                      </span>
                      <i className="bar"></i>
                    </div>
                  )}

                  {showPasswordField && showOtpAlternatives && (
                    <div className="auth-field-or">OR</div>
                  )}

                  {showOtpAlternatives && (
                    <div className="login-otp-options">
                      {showPasswordField && (
                        <span className="login-otp-options__label">One-time code</span>
                      )}
                      <div className="login-otp-options__links">
                        {showPhoneOtpOptions && (
                          <>
                            <button
                              type="button"
                              className="login-otp-options__btn"
                              disabled={loading}
                              onClick={async () => {
                                if (loading) return;
                                setLoading(true);
                                setResponseMessage('');
                                try {
                                  await requestLoginOtpAndNavigate({
                                    type: 'phone',
                                    identifier: formData.phone,
                                    navigate,
                                    sendFn: () => resendOtpByPhone({ phone: formData.phone, purpose: 'login' }),
                                  });
                                } catch (err) {
                                  const msg = err.response?.data?.msg || err.response?.data?.message;
                                  setResponseMessage(msg || 'Could not send OTP. Please try again.');
                                } finally {
                                  setLoading(false);
                                }
                              }}
                            >
                              <i className="bi bi-phone"></i>
                              Phone OTP
                            </button>
                            <span className="login-otp-options__divider">|</span>
                            <button
                              type="button"
                              className="login-otp-options__btn"
                              disabled={loading}
                              onClick={async () => {
                                if (loading) return;
                                setLoading(true);
                                setResponseMessage('');
                                try {
                                  await requestLoginOtpAndNavigate({
                                    type: 'whatsapp',
                                    identifier: formData.phone,
                                    navigate,
                                    sendFn: async () => {
                                      const data = await resendOtpByWhatsApp({ phone: formData.phone, purpose: 'login' });
                                      setPreferredNotifyChannel('whatsapp');
                                      return data;
                                    },
                                  });
                                } catch (err) {
                                  const msg = err.response?.data?.msg || err.response?.data?.message;
                                  setResponseMessage(msg || 'Could not send WhatsApp OTP. Join the Twilio sandbox first, then try again.');
                                } finally {
                                  setLoading(false);
                                }
                              }}
                            >
                              <i className="bi bi-whatsapp"></i>
                              WhatsApp OTP
                            </button>
                          </>
                        )}
                        {showEmailOtpOption && (
                          <button
                            type="button"
                            className="login-otp-options__btn"
                            disabled={loading}
                            onClick={async () => {
                              if (loading) return;
                              setLoading(true);
                              setResponseMessage('');
                              try {
                                await requestLoginOtpAndNavigate({
                                  type: 'email',
                                  identifier: formData.email.trim(),
                                  navigate,
                                  sendFn: () => resendOtpByEmail({ email: formData.email.trim(), purpose: 'login' }),
                                });
                              } catch (err) {
                                const msg = err.response?.data?.msg || err.response?.data?.message;
                                setResponseMessage(msg || 'Could not send code. Please try again.');
                              } finally {
                                setLoading(false);
                              }
                            }}
                          >
                            <i className="bi bi-envelope"></i>
                            Email code
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {responseMessage && (
                <div className="responseMessage">
                  <i className="bi bi-exclamation-circle-fill"></i>
                  <span>{responseMessage}</span>
                </div>
              )}

              <button type="submit" className="submitBottomOption" disabled={loading || isPasswordCheckPending}>
                {getSubmitButtonLabel()}
              </button>

              <div className="mob-auth-divider mob-auth-divider--pill">
                <span className="mob-auth-divider__text">OR</span>
              </div>

              <div className="mob-oauth-buttons">
                <GoogleSignInButton
                  variant="mob"
                  disabled={loading}
                  onError={(msg) => setResponseMessage(msg)}
                />
              </div>

              <div className="mob-auth-divider mob-auth-divider--pill">
                <span className="mob-auth-divider__text">OR</span>
              </div>

              <button type="button" className="mob-guest-btn" onClick={() => setShowGuestForm(true)}>
                LOGIN AS GUEST
              </button>
            </form>
            {showGuestForm && (
              <GuestForm onClose={() => setShowGuestForm(false)} onSuccess={() => setShowGuestForm(false)} />
            )}
          </div>

          <div className="get-help">
            Don&apos;t have an account? <Link to="/auth/register">Register</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
